import React, {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {useReactToPrint} from "react-to-print";

import { TableView as TableViewIcon } from '@vedmarket/ui-kit';
import { PictureAsPdf as PictureAsPdfIcon } from '@vedmarket/ui-kit';
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import {
  useProductsList,
  useUpdateProductsList,
  INITIAL_DATA_REQUEST,
  useLazyProductsDownloadExcelByInvoiceIdQuery,
  useLazyProductsSearchByInvoiceIdQuery,
} from "../../../../../../../../../../../api/calc";
import Preloader from "../../../../../../../../../../common/preloader";

import ProductListTable from "./components/product_list_table";
import PrintTable from "./components/print_table";
import {ACTIVE_COLUMNS_BY_DEFAULT} from "./constants";
import {COLORS} from "../../../../../../../../../../../config/constants";

import './products_table.scss';

type Props = {
  invoiceId: number;
  onEdit(id: number): void
  onRemove(id: number): void
  title: string
}

const enum PRINT_ORIENTATION {
  VERTICAL = 'VERTICAL',
  HORIZONTAL = 'HORIZONTAL',
}

const ProductsTable = ({invoiceId, onEdit, onRemove, title}: Props) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const [ currentProductsList, setCurrentProductsList ] = useState<string[]>([])

  const [ action, setAction ] = React.useState<string>('');

  const [ productsListRequest, { isLoading: isLoadingProductsListRequest } ] = useLazyProductsSearchByInvoiceIdQuery()

  const [ downloadExcelRequest ] = useLazyProductsDownloadExcelByInvoiceIdQuery();

  const productsListParams = useProductsList()?.params;

  const [ hasLoadedFirstRequest, setHasLoadedFirstRequest ] = useState(false)
  const [ printOrientation, setPrintOrientation ] = useState<PRINT_ORIENTATION | null>(null)

  const [ activeColumns, setActiveColumns ] = useState<string[]>(ACTIVE_COLUMNS_BY_DEFAULT)

  const componentRef = useRef();

  const productsListRequestUpdate = (params?: DictionaryParamsInput) => {
    return productsListRequest({...productsListParams, ...params});
  }

  useEffect(() => {
    productsListRequest({...INITIAL_DATA_REQUEST, invoiceId: invoiceId }).then(() =>
      setHasLoadedFirstRequest(true)
    )
  }, [])

  useEffect(() => {
    return () => {
      // @ts-ignore
      dispatch(useUpdateProductsList());
    };
  }, []);

  useEffect(() => {
    if (printOrientation !== null) {
      handlePrint();
    }
  }, [ printOrientation ]);

  const productsListRequestSearch = (query: string) => productsListRequest({
    ...productsListParams,
    query,
  })

  const isLoadingStatus = isLoadingProductsListRequest && !hasLoadedFirstRequest;

  const downloadExcel = async () => {
    try {
      const { blob, filename } = await downloadExcelRequest({ invoiceId }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  }

  const getPageMargins = () => {
    const printPageOrientation = printOrientation === PRINT_ORIENTATION.VERTICAL ? 'portrait' : 'landscape';
    return `@page { margin: 32px 48px !important; size: A4 ${printPageOrientation}; }`;
  }

  const handlePrintVertical = () => {
    setPrintOrientation(PRINT_ORIENTATION.VERTICAL);
    handlePrint();
  }

  const handlePrintHorizontal = () => {
    setPrintOrientation(PRINT_ORIENTATION.HORIZONTAL);
    handlePrint();
  }

  const handlePrint = useReactToPrint({
    // @ts-ignore
    content: () => componentRef.current,
    documentTitle: title
  });

  return (
    <>
      <div className='product-table-container'>
        {isLoadingStatus ? <Preloader/> : <>
          <ProductListTable
            onEdit={onEdit}
            onRemove={onRemove}
            handleProductsListRequest={productsListRequestUpdate}
            onSearch={productsListRequestSearch}
            chooseProduct={setCurrentProductsList}
            currentProducts={currentProductsList}
            setActiveColumns={setActiveColumns}
            activeColumns={activeColumns}
          />
          <div className='product-table-container__actions'>
            <IconButton
              onClick={downloadExcel}
              className='product-table-container__button'>
              <TableViewIcon style={{ fill: COLORS.BLUE }} />
              <Typography variant='body1' sx={{ color: COLORS.BLUE, marginLeft: 1 }}>
                {t('common__text__downloadExcel')}
              </Typography>
            </IconButton>
            <IconButton
              onClick={handlePrintVertical}
              className='product-table-container__button'>
              <PictureAsPdfIcon style={{ fill: COLORS.BLUE }} />
              <Typography variant='body1' sx={{ color: COLORS.BLUE, marginLeft: 1 }}>
                {t('common__text__downloadPDFVertical')}
              </Typography>
            </IconButton>
            <IconButton
              onClick={handlePrintHorizontal}
              className='product-table-container__button'>
              <PictureAsPdfIcon style={{ fill: COLORS.BLUE }} />
              <Typography variant='body1' sx={{ color: COLORS.BLUE, marginLeft: 1 }}>
                {t('common__text__downloadPDFHorizontal')}
              </Typography>
            </IconButton>
          </div>
        </>}
      </div>
      <PrintTable
        ref={componentRef}
        isHorizontal={printOrientation === PRINT_ORIENTATION.HORIZONTAL}
        title={title}
        currentProducts={currentProductsList}
        setActiveColumns={setActiveColumns}
        activeColumns={activeColumns}
      />
      <style>{getPageMargins()}</style>
    </>
  )
};

export default ProductsTable
